import * as S from "../styles";

import { Autocomplete, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, createFilterOptions } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";
import AutoCompleteMult from "@/components/hookForms/autoCompleteMult";
import CheckBox from "@/components/hookForms/checkbox";
import DatePickerMui from "@/components/hookForms/datePicker";
import IGenericResult from "@/interfaces/IGenericResult";
import messages from "@/utils/messages";
import serviceClientes from "@/services/serviceCliente";
import serviceConcessionaria from "@/services/serviceConcessionaria";
import serviceRelatorio from "@/services/serviceRelatorio";
import serviceUsina from "@/services/serviceUsina";
import BuscaCliente from "@/components/personSearchCheckbox";
import AutoCompleteCheckbox from "@/components/hookForms/autoCompleteCheckbox";
import { LoadingButton } from "@mui/lab";
import Parametrizado from "./parametrizado";
import { useAuth } from "@/context/global";
import { IWhiteLabelReturn } from "@/interfaces/IWhiteLabel";

const filterOptions = createFilterOptions({
    ignoreCase: true,
    matchFrom: "any",
    limit: 1000,
});

// Perfil "Consulta" só pode emitir o relatório Geral através de um modelo
// personalizado específico (relatorioWhiteLabelID 17).
const RELATORIO_WHITE_LABEL_CONSULTA_ID = 17;

export interface IDialogProps {
    open: boolean;
    onClose?: any;
    instalacaoOptions?: IGenericResult[];
}

export default function Geral({ open, onClose, instalacaoOptions: instalacaoDataOptions = [] }: IDialogProps) {
    const [usinaDataOptions, setUsinaDataOptions] = useState<IGenericResult[]>([]);
    const [clienteOptions, setClienteOptions] = useState<IGenericResult[]>([]);
    const [concessionariaDataOptions, setConcessionariaDataOptions] = useState<IGenericResult[]>([]);
    const [selectedClientIds, setSelectedClientIds] = useState<any[]>([]);
    const [disabled, setDisabled] = useState(false);
    const [loading, setLoading] = useState(false);
    const history = useHistory();
    const [searchClient, setSearchClient] = useState({ open: false });
    const [openParameters, setParameters] = useState(false)
    const { user } = useAuth()
    const [relSelcted, setRelSelected] = useState<{ value: number; label: string } | null>(null)
    const [opt, setOpt] = useState<Array<{ value: number; label: string }>>([])
    const [res, setRes] = useState<IWhiteLabelReturn[]>([])
    const [listaCampos, setListaCampos] = useState("")
    const clienteOptionsMemo = useMemo(() => clienteOptions, [clienteOptions]);
    const isConsulta = user?.perfil?.toLowerCase() === "consulta";
    const modelosOptions = useMemo(
        () => (isConsulta ? opt.filter((x) => x.value === RELATORIO_WHITE_LABEL_CONSULTA_ID) : opt),
        [isConsulta, opt]
    );

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        formState: { errors },
    } = useForm({
        defaultValues: {
            dataInicio: "",
            dataPublicacaoInicio: "",
            dataFim: "",
            dataPublicacaoFim: "",
            idsInstalacoes: "",
            idsClientes: "",
            idsUsinas: "",
            idsConcessionarias: "",
        },
    });

    const onSubmit = (values: any) => {
        if (isConsulta && relSelcted?.value !== RELATORIO_WHITE_LABEL_CONSULTA_ID) {
            toast.error("Seu perfil só permite gerar este relatório pelo modelo personalizado disponível.");
            return;
        }

        setDisabled(true);
        setLoading(true);

        const newValues = {
            ...values,
            relatorioTipoID: 1,
            relatorioWhiteLabelID: relSelcted ? relSelcted.value : "",
            dataInicio: values.dataInicio ? format(values.dataInicio, "yyyy-MM-01") : "",
            dataPublicacaoInicio: values.dataPublicacaoInicio ? format(values.dataPublicacaoInicio, "yyyy-MM-dd") : "",
            dataFim: values.dataFim ? format(values.dataFim, "yyyy-MM-01") : "",
            dataPublicacaoFim: values.dataPublicacaoFim ? format(values.dataPublicacaoFim, "yyyy-MM-dd") : "",
            idsInstalacoes: values.idsInstalacoes ? values.idsInstalacoes.join(", ") : "",
            idsClientes: values.idsClientes ? values.idsClientes.join(", ") : "",
            idsUsinas: values.idsUsinas ? values.idsUsinas.join(", ") : "",
            idsConcessionarias: values.idsConcessionarias ? values.idsConcessionarias.join(", ") : "",
        };

        serviceRelatorio
            .getRelatorioGeral(newValues)
            .then((result) => {
                if (result) {
                    onClose();
                    toast.success(messages.reportSucess);
                }
            })
            .catch(() => {
                toast.error(messages.reportError);
            })
            .finally(() => {
                setDisabled(false);
                setLoading(false);
                if (newValues.redirecionar) {
                    history.push("/relatorios/listagemrelatorios");
                }
            });
    };

    useEffect(() => {
        let isMounted = true;

        const setUsinaData = async () => {
            try {
                const result = await serviceUsina.getUsinas();
                if (isMounted) setUsinaDataOptions(result?.data || []);
            } catch {
                if (isMounted) setUsinaDataOptions([]);
            }
        };

        const setClienteData = async () => {
            try {
                const result = await serviceClientes.getClientes();
                if (isMounted) setClienteOptions(result?.data || []);
            } catch {
                if (isMounted) setClienteOptions([]);
            }
        };

        const setConcessionariaData = async () => {
            try {
                const result = await serviceConcessionaria.getConcessionarias();
                if (isMounted) setConcessionariaDataOptions(result?.data || []);
            } catch {
                if (isMounted) setConcessionariaDataOptions([]);
            }
        };

        const fetchData = async () => {
            try {
                const { data } = await serviceRelatorio.getRelatorioPersonalizado();
                const newOptions = data
                    .filter((y) => y.habilitado.toLowerCase() === "sim")
                    .map((x) => ({
                        value: x.relatorioWhiteLabelID,
                        label: x.nomeRelatorioWhiteLabel,
                    }));
                if (isMounted) {
                    setOpt(newOptions)
                    setRes(data)
                };
            } catch {
                if (isMounted) setOpt([]);
            }
        };

        const init = async () => {
            await setUsinaData();
            await setClienteData();
            await setConcessionariaData();
            await fetchData();
        };

        init();

        return () => {
            isMounted = false;
        };
    }, []);

    // Perfil "Consulta": o modelo permitido já vem selecionado e não pode ser trocado.
    useEffect(() => {
        if (!isConsulta) return;
        const modeloPermitido = modelosOptions.find((x) => x.value === RELATORIO_WHITE_LABEL_CONSULTA_ID);
        setRelSelected((atual) => (atual?.value === RELATORIO_WHITE_LABEL_CONSULTA_ID ? atual : modeloPermitido ?? null));
    }, [isConsulta, modelosOptions]);

    const handleOpenModalParametros = () => setParameters(true)
    const handleCloseModalParametros = () => setParameters(false)

    useEffect(() => {
        if (relSelcted && res.length > 0) {
            const match = res.find(x => x.relatorioWhiteLabelID === relSelcted.value);
            if (match) {
                setListaCampos(match.listaCampos);
            } else {
                setListaCampos("");
            }
        }
    }, [relSelcted, res]);

    return (
        <div>
            <Dialog
                maxWidth="md"
                fullWidth
                onClose={(_, reason) => {
                    if (reason !== "backdropClick") {
                        onClose();
                    }
                }}
                open={open}
            >
                <DialogTitle >
                    <S.Title>Gerar relatório: Geral</S.Title>
                    <IconButton
                        aria-label="close"
                        onClick={() => {
                            reset();
                            onClose(onclose);
                        }}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <DialogContent dividers>
                        <S.Subtitles>Modelos Personalizados</S.Subtitles>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={12}>
                                <Autocomplete
                                    size="small"
                                    options={modelosOptions}
                                    disableClearable={isConsulta}
                                    readOnly={isConsulta}
                                    value={relSelcted}
                                    onChange={(_, newValue) => {
                                        setRelSelected(newValue);
                                        if (!newValue) {
                                            setListaCampos("");
                                        } else {
                                            const found = res.find(x => x.relatorioWhiteLabelID === newValue.value);
                                            if (found) setListaCampos(found.listaCampos || "");
                                        }
                                    }}
                                    getOptionLabel={(option) => option.label}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                    renderOption={(props, option) => (
                                        <li {...props} key={option.value}>
                                            {option.label}
                                        </li>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            margin="normal"
                                            size="small"
                                            label="Modelos"
                                            placeholder="Selecione"
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                        {(!(listaCampos.includes("dataInicio") || listaCampos.includes("dataFim"))) && (
                            <S.Subtitles>Data</S.Subtitles>
                        )}
                        <Grid container spacing={2}>
                            {!(listaCampos.includes("dataInicio")) && (

                                <Grid item xs={12} md={6}>
                                    <DatePickerMui
                                        name="dataInicio"
                                        label="Inicio"
                                        format="MM/yyyy"
                                        view={["month", "year"]}
                                        control={control}
                                        error={errors}
                                    />
                                </Grid>
                            )}
                            {!(listaCampos.includes("dataFim")) && (
                                <Grid item xs={12} md={6}>
                                    <DatePickerMui
                                        name="dataFim"
                                        label="Fim"
                                        format="MM/yyyy"
                                        view={["month", "year"]}
                                        control={control}
                                        error={errors}
                                    />
                                </Grid>
                            )}
                        </Grid>

                        {(!(listaCampos.includes("dataPublicacaoInicio") || listaCampos.includes("dataPublicacaoFim"))) && (
                            <S.Subtitles>Publicação</S.Subtitles>
                        )}
                        <Grid container spacing={2}>
                            {!listaCampos.includes("dataPublicacaoInicio") && (
                                <Grid item xs={12} md={6}>
                                    <DatePickerMui
                                        id="dataPublicacaoInicio"
                                        name="dataPublicacaoInicio"
                                        label="Inicio"
                                        format="dd/MM/yyyy"
                                        view={["day", "month", "year"]}
                                        control={control}
                                        error={errors}
                                    />
                                </Grid>
                            )}
                            {!listaCampos.includes("dataPublicacaoFim") && (
                                <Grid item xs={12} md={6}>
                                    <DatePickerMui
                                        id="dataPublicacaoFim"
                                        name="dataPublicacaoFim"
                                        label="Fim"
                                        format="dd/MM/yyyy"
                                        view={["day", "month", "year"]}
                                        control={control}
                                        error={errors}
                                    />
                                </Grid>
                            )}
                        </Grid>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={12}>
                                {!listaCampos.includes("idsInstalacoes") && (
                                    <AutoCompleteMult
                                        name="idsInstalacoes"
                                        label="Instalação"
                                        options={instalacaoDataOptions}
                                        filterOptions={filterOptions}
                                        disableCloseOnSelect={true}
                                        control={control}
                                        error={errors}
                                        concatWithID={true}
                                        limitTags={2}
                                    />
                                )}
                            </Grid>
                            <Grid item xs={12} md={12}>
                                {!listaCampos.includes("idsClientes") && (
                                    <AutoCompleteCheckbox
                                        name="idsClientes"
                                        label="Cliente"
                                        readOnly={true}
                                        options={selectedClientIds}
                                        limitTags={2}
                                        control={control}
                                        error={errors}
                                        onClick={() =>
                                            setSearchClient({
                                                open: true,
                                            })
                                        }
                                    />
                                )}
                            </Grid>
                            <Grid item xs={12} md={12}>
                                {!listaCampos.includes("idsUsinas") && (
                                    <AutoCompleteMult
                                        name="idsUsinas"
                                        label="Nome da Usina"
                                        options={usinaDataOptions}
                                        control={control}
                                        error={errors}
                                    />
                                )}
                            </Grid>
                            <Grid item xs={12} md={12}>
                                {!listaCampos.includes("idsConcessionarias") && (
                                    <AutoCompleteMult
                                        name="idsConcessionarias"
                                        label="Concessionarias"
                                        options={concessionariaDataOptions}
                                        control={control}
                                        error={errors}
                                    />
                                )}
                            </Grid>
                            <Grid item xs={12} md={12}>
                                <CheckBox
                                    name="redirecionar"
                                    label="Redirecionar para a tela de relatório"
                                    control={control}
                                    error={errors}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions style={{ display: "flex", justifyContent: "center", margin: "24px" }}>
                        <Button
                            style={{ width: "159px", height: "44px" }}
                            onClick={() => {
                                reset();
                                onClose(onClose);
                            }}
                            color="warning"
                            variant="contained"
                        >
                            Cancelar
                        </Button>
                        <LoadingButton
                            loading={loading}
                            disabled={disabled}
                            style={{ width: "159px", height: "44px" }}
                            type="submit"
                            variant="contained"
                        >
                            Gerar
                        </LoadingButton>
                        {(!user || user.perfil === "Master" || user.perfil === "Admin") && (
                            <Button
                                style={{ width: "180px", height: "44px", marginLeft: "16px" }}
                                type="button"
                                variant="outlined"
                                color="primary"
                                onClick={handleOpenModalParametros}
                            >
                                Gerenciar Modelos
                            </Button>
                        )}

                    </DialogActions>
                </form>
            </Dialog>
            <BuscaCliente
                open={searchClient.open}
                clienteOptions={clienteOptionsMemo}
                clientsSelecteds={(item: any) => {
                    setSelectedClientIds(
                        item.map((data) => {
                            return { label: data.label, value: data.value };
                        })
                    );
                    setValue(
                        "idsClientes",
                        item.map((data) => data.value.toString())
                    );
                }}
                onClose={() =>
                    setSearchClient({
                        open: false,
                    })
                }
            />
            <Parametrizado open={openParameters} onClose={handleCloseModalParametros} />
        </div>

    );
}
